/**
 * School Creation Form Component
 *
 * Reference implementation following UI Standards (see /UI_STANDARDS.md)
 *
 * Standards Applied:
 * - Stack layout with spacing={3} for form fields
 * - Responsive Stack direction={{ xs: 'column', md: 'row' }}
 * - Consistent spacing: mb: 3 for sections, p: 4 for Paper
 * - Breadcrumb navigation for nested pages
 * - Page header with h4 title + body2 description
 * - Success/Error alerts with mb: 3
 * - Client-side validation with real-time error clearing
 * - Loading states on buttons and form fields
 * - FastAPI error handling (string and array formats)
 * - Auto-redirect after successful creation
 * - Proper TypeScript types for all state
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link as MuiLink,
  Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { SchoolCreate } from '../../types/school';

const SchoolForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<SchoolCreate>({
    name: '',
    address: '',
    phone: '',
    email: '',
    ieb_centre_number: '',
    reseller_id: null,
    github_folder_structure_id: null,
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'School name is required';
    }

    if (!formData.address || formData.address.trim() === '') {
      errors.address = 'Address is required';
    }

    if (!formData.phone || formData.phone.trim() === '') {
      errors.phone = 'Phone number is required';
    }

    if (!formData.email || formData.email.trim() === '') {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? null : value
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      // Convert empty strings to null for optional fields
      const payload = {
        ...formData,
        ieb_centre_number: formData.ieb_centre_number || null,
        reseller_id: formData.reseller_id || null,
        github_folder_structure_id: formData.github_folder_structure_id || null,
      };

      await axios.post('/api/schools/', payload);

      setSuccess(true);

      // Redirect to schools list after 1.5 seconds
      setTimeout(() => {
        navigate('/schools');
      }, 1500);
    } catch (err: any) {
      console.error('Error creating school:', err);

      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else if (Array.isArray(err.response.data.detail)) {
          // Handle FastAPI validation errors
          const errorMessages = err.response.data.detail
            .map((e: any) => `${e.loc.join('.')}: ${e.msg}`)
            .join(', ');
          setError(errorMessages);
        } else {
          setError('Failed to create school. Please check your input.');
        }
      } else {
        setError('Failed to create school. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/schools');
  };

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/schools')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Schools
        </MuiLink>
        <Typography color="text.primary">New School</Typography>
      </Breadcrumbs>

      {/* Page Header */}
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Create New School
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Enter the details for the new school institution
        </Typography>
      </Box>

      {/* Success Message */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          School created successfully! Redirecting to schools list...
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Row 1: School Name and Email */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                required
                label="School Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
                disabled={loading}
              />
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={!!validationErrors.email}
                helperText={validationErrors.email}
                disabled={loading}
              />
            </Stack>

            {/* Row 2: Address */}
            <TextField
              fullWidth
              required
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              error={!!validationErrors.address}
              helperText={validationErrors.address}
              disabled={loading}
              multiline
              rows={2}
            />

            {/* Row 3: Phone and IEB Centre Number */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                required
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                error={!!validationErrors.phone}
                helperText={validationErrors.phone}
                disabled={loading}
              />
              <TextField
                fullWidth
                label="IEB Centre Number"
                name="ieb_centre_number"
                value={formData.ieb_centre_number || ''}
                onChange={handleChange}
                disabled={loading}
                helperText="Optional - For schools registered with IEB"
              />
            </Stack>

            {/* Row 4: Reseller ID and GitHub Folder Structure ID */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                type="number"
                label="Reseller ID"
                name="reseller_id"
                value={formData.reseller_id || ''}
                onChange={handleChange}
                disabled={loading}
                helperText="Optional - Associated reseller identifier"
              />
              <TextField
                fullWidth
                type="number"
                label="GitHub Folder Structure ID"
                name="github_folder_structure_id"
                value={formData.github_folder_structure_id || ''}
                onChange={handleChange}
                disabled={loading}
                helperText="Optional - GitHub folder structure template"
              />
            </Stack>

            {/* Action Buttons */}
            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Creating...' : 'Create School'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default SchoolForm;
